import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarPlus } from "lucide-react";

const L = "text-[10px] uppercase tracking-[0.2em] font-semibold";
const defaultSlot = () => { const d = new Date(Date.now() + 2 * 86400000); d.setHours(10, 0, 0, 0); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16); };

export const InterviewDialog = ({ open, onOpenChange, candidate, interview, modes, onSaved }) => {
  const [form, setForm] = useState({ scheduled_at: defaultSlot(), duration_min: 45, mode: "visio", location_or_link: "", interviewers: "", notes: "", send_convocation: true });
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    if (interview) {
      const local = new Date(interview.scheduled_at);
      setForm({ scheduled_at: new Date(local.getTime() - local.getTimezoneOffset() * 60000).toISOString().slice(0, 16), duration_min: interview.duration_min, mode: interview.mode,
                location_or_link: interview.location_or_link || "", interviewers: (interview.interviewers || []).join(", "), notes: interview.notes || "", send_convocation: false });
    } else setForm({ scheduled_at: defaultSlot(), duration_min: 45, mode: "visio", location_or_link: "", interviewers: "", notes: "", send_convocation: !!candidate?.email });
  }, [open, interview, candidate]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...form, scheduled_at: new Date(form.scheduled_at).toISOString(), duration_min: Number(form.duration_min) || 45,
                        interviewers: form.interviewers.split(",").map((s) => s.trim()).filter(Boolean) };
      if (interview) {
        delete payload.send_convocation;
        await api.patch(`/admin/recrutement/interviews/${interview.id}`, payload);
        toast.success("Entretien modifié");
      } else {
        await api.post(`/admin/recrutement/candidates/${candidate.id}/interviews`, payload);
        toast.success(form.send_convocation ? "Entretien planifié — convocation envoyée" : "Entretien planifié");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="interview-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-black flex items-center gap-2"><CalendarPlus className="h-5 w-5 text-[#7C3AED]" />{interview ? "Modifier l'entretien" : "Planifier un entretien"}</DialogTitle>
          <DialogDescription className="text-xs">{candidate?.prenom} {candidate?.nom} · {candidate?.offer_title}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className={L}>Date & heure *</Label><Input type="datetime-local" value={form.scheduled_at} onChange={(e) => set("scheduled_at")(e.target.value)} data-testid="itw-datetime" /></div>
          <div className="space-y-1.5"><Label className={L}>Durée (min)</Label><Input type="number" min="10" max="480" value={form.duration_min} onChange={(e) => set("duration_min")(e.target.value)} data-testid="itw-duration" /></div>
          <div className="space-y-1.5"><Label className={L}>Mode</Label>
            <select value={form.mode} onChange={(e) => set("mode")(e.target.value)} data-testid="itw-mode" className="h-10 w-full border border-[#DEE2E6] bg-white px-2 text-sm">
              {Object.entries(modes || {}).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label className={L}>{form.mode === "visio" ? "Lien visio" : form.mode === "telephone" ? "Numéro appelé" : "Adresse"}</Label><Input value={form.location_or_link} onChange={(e) => set("location_or_link")(e.target.value)} data-testid="itw-place" /></div>
          <div className="col-span-2 space-y-1.5"><Label className={L}>Interlocuteurs PAG (virgules)</Label><Input value={form.interviewers} onChange={(e) => set("interviewers")(e.target.value)} placeholder="Marie D., Karim B." data-testid="itw-interviewers" /></div>
          <div className="col-span-2 space-y-1.5"><Label className={L}>Informations pour le candidat</Label><Textarea value={form.notes} onChange={(e) => set("notes")(e.target.value)} rows={3} placeholder="Pièces à apporter, déroulé, personne à demander…" data-testid="itw-notes" /></div>
          {!interview && (
            <label className="col-span-2 flex items-center gap-2 text-xs cursor-pointer">
              <Checkbox checked={form.send_convocation} onCheckedChange={(v) => set("send_convocation")(!!v)} data-testid="itw-send" />
              Envoyer la convocation maintenant (email{candidate?.telephone ? " + SMS si Twilio est configuré" : ""})
            </label>
          )}
        </div>
        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={save} disabled={saving || !form.scheduled_at} className="bg-[#7C3AED] text-white" data-testid="itw-save">{saving ? "Enregistrement…" : interview ? "Enregistrer" : "Planifier"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
